<script setup>
import { ref, computed, watch, defineProps } from 'vue'
import { useProductListStore } from '@/store'

const props = defineProps({
  item: Object
})

// Initialize the store
const store = useProductListStore()

// Local state
const isOnlineAvailable = ref(false)
const isOutOfStock = ref(false)
const isNotOnlineAvailable = ref(false)

// Computed properties
const detailPageUrl = computed(() => store.detailPageUrl)
const apiSite = computed(() => store.apiSite)
const bazaarvoiceEnabled = computed(() => store.bazaarvoiceEnabled)
const layout = computed(() => store.layout)
const showPrice = computed(() => store.showPrice)
const showBuyButton = computed(() => store.showBuyButton)
const showBuyButtonOnThisWidget = computed(() => store.showBuyButtonOnThisWidget)
const showButtonStyle = computed(() => store.showButtonStyle)
const showComparelistButton = computed(() => store.showComparelistButton)
const shopEnabled = computed(() => store.shopEnabled)
const syliusLoaded = computed(() => store.syliusLoaded)
const locale = computed(() => store.locale)
const localeHref = computed(() => store.localeHref)

// Methods
function calcWidth(num) {
  return (num / 5) * 100
}

function setItemAvailabilities(cardRef) {
  if (cardRef.value) {
    isOnlineAvailable.value = cardRef.value.classList.contains('-available')
    isOutOfStock.value = cardRef.value.classList.contains('-out-of-stock')
    isNotOnlineAvailable.value = cardRef.value.classList.contains('-not-online')
  }
}

function addToCompareList(itemNo) {
  store.addToCompareList(itemNo)
}

const showLabel = computed(() =>
{
  const endDate = new Date(props.item.icons[0].endDate).toISOString()
  const startDate = new Date(props.item.icons[0].startDate).toISOString()
  const currentDate = new Date().toISOString()
  return currentDate <= endDate && currentDate >= startDate
})

const iconLabel = computed(() =>
{
  return props.item.icons[0].labelTranslations[localeHref.value] != null ? props.item.icons[0].labelTranslations[localeHref.value] : props.item.icons[0].labelTranslations[locale.value]
})

const iconColor = computed(() =>
{
  return props.item.icons[0].labelTranslations.de == 'SALE' || props.item.icons[0].labelTranslations.de == 'AKTION' ? '-bg-alpha' : '-bg-black'
})

watch(syliusLoaded, () => {
  setItemAvailabilities(card)
})

const card = ref(null)
</script>

<template>
  <div ref="card" class="card__card -layout-two card__card--vue" v-if="layout === '2'" :data-itemno="item.itemNo">
    <a v-if="showButtonStyle !== '1'" :href="detailPageUrl + item.slug + '/'" :title="($t('card.detailLink.titlePrefix') + ' ' + item.description)"><span></span></a>
    <div class="card-wrapper matomo-ecommerce" :data-matomo-name="item.description" :data-matomo-sku="item.itemNo"
         :data-matomo-category="item.tpgCode">
      <div class="card__top">
          <a v-if="showButtonStyle === '1'" :href="detailPageUrl + item.slug + '/'" :title="($t('card.detailLink.titlePrefix') + ' ' + item.description)"><span></span></a>
          <div class="image-wrapper" v-if="item.productImageData && item.productImageData.imageUrl">
            <div class="top__image">
              <img :src="item.productImageData.imageUrl + '-FWEBP-B400'" :alt="item.imageTitle" loading="lazy">
              <div class="product-badge" v-if="item.icons && item.icons.length && showLabel">
                <div class="badge__badge" :class="iconColor">
                  <span class="-color-white meta bold">{{ iconLabel }}</span>
                </div>
              </div>
            </div>
          </div>
        <div class="top__ranking" v-if="bazaarvoiceEnabled === '1'">
          <div v-if="apiSite === 'ozito.com.au' || apiSite === 'ozito.co.nz'" data-bv-show="inline_rating" :data-bv-product-id="item.description"></div>
          <div v-else data-bv-show="inline_rating" :data-bv-product-id="item.itemNo"></div>
        </div>
        <div class="top__ranking" v-else-if="item.review && ratingEnabled == '1'">
          <div class="ranking__stars">
            <div class="stars__stars stars__red" :style="'width:' + calcWidth(item.review.avg) + '%'"></div>
          </div>
          <div class="ranking__count">
            <span class="meta -color-grey">({{item.review.count}})</span>
          </div>
        </div>
        <div class="top__title">
            <span class="meta bold -color-text professional">
               <span v-if="item.professionals">{{ $t('card.professionals')}}</span>
            </span>
          <span class="meta bold -color-text -tpg-name">{{item.tpgName}}</span>
          <span class="heading epsilon -ozito-title" v-if="item.webSiteOzitoTitle">{{item.webSiteOzitoTitle}}</span>
          <span class="heading epsilon" v-if="!item.webSiteOzitoTitle">{{item.description}}</span>
          <span class="meta -color-grey bold">{{ $t('card.itemNo')}}.: {{item.itemNo}}</span>
        </div>
      </div>
      <div class="card__mid" v-if="shopEnabled === '1'">
        <div class="mid__content">
          <span v-if="isOnlineAvailable" class="item__availability meta bold">{{ $t('card.availability.available')}}</span>
          <span v-if="isOutOfStock" class="item__availability meta bold">{{ $t('card.availability.outofstock')}}</span>
          <span v-if="isNotOnlineAvailable" class="item__availability meta bold">{{ $t('card.availability.notonline')}}</span>
        </div>
      </div>
      <div class="card__bottom">
        <div class="bottom__content">
          <div class="content__left" :class="{'-hide': showBuyButtonOnThisWidget !== '1'}" v-if="showButtonStyle === '1'">
            <div :data-itemno="item.itemNo" class="-btn-alpha checkout__button" v-if="showBuyButton === '1'" tabindex="0"><span>{{ $t('card.buy')}}</span></div>
            <div v-if="shopEnabled !== '1'" class="not-available__button"><span>{{ $t('card.notavailable')}}</span></div>
          </div>
          <div class="content__left" v-else-if="apiSite === 'ozito.com.au' || apiSite === 'ozito.co.nz'">
            <span class="meta -ozito-title -color-grey" v-if="item.webSiteOzitoTitle">{{item.description}}</span>
<!--            <span class="right_prices">-->
<!--              <span class="strikethrough meta -hide">-->
<!--                <span class="rrp" v-if="item.rrp || item.price">{{ $t('card.price')}}</span>&nbsp;-->
<!--                <span class="-line-through" v-if="item.rrp" :data-price="item.rrp.price">{{item.rrp.price}} {{item.rrp.currency}}</span>-->
<!--                <span class="-line-through" v-else-if="item.price" :data-price="item.price.price">{{item.price.price}} {{item.price.currency}}</span>-->
<!--              </span>-->
<!--              <span class="price__highlighted">-->
<!--                <span class="price__price" v-if="item.rrp" :data-price="item.rrp.price">{{item.rrp.price}}</span> <span class="currency" v-if="item.rrp">{{item.rrp.currency}}</span>-->
<!--                <span class="price__price" v-else-if="item.price" :data-price="item.price.price">{{item.price.price}}</span> <span class="currency" v-if="item.price && !item.rrp">{{item.price.currency}}</span>-->
<!--                <span class="price__price" v-else-if="!item.rrp && !item.price"></span>-->
<!--              </span>-->
<!--            </span>-->
          </div>
          <div class="content__left" :class="{'-hide': showBuyButtonOnThisWidget !== '1'}" v-else>
            <div :data-itemno="item.itemNo" class="-lnk-beta checkout__button" v-if="showBuyButton === '1'" tabindex="0"><span class="heading -color-alpha">{{ $t('card.buy')}}</span></div>
            <div v-if="shopEnabled !== '1'" class="not-available__button"><span>{{ $t('card.notavailable')}}</span></div>
          </div>
          <div class="content__right">
            <span class="right_prices" v-if="shopEnabled === '1'">
              <span class="strikethrough meta -hide">
                <span class="rrp" v-if="item.rrp || item.price">{{ $t('card.price')}}</span>&nbsp;
                <span class="-line-through" v-if="item.rrp" :data-price="item.rrp.price">{{item.rrp.price}} {{item.rrp.currency}}</span>
                <span class="-line-through" v-else-if="item.price" :data-price="item.price.price">{{item.price.price}} {{item.price.currency}}</span>
              </span>
              <span class="price__highlighted">
                <span class="price__price" v-if="item.rrp" :data-price="item.rrp.price">{{item.rrp.price}}</span> <span class="currency" v-if="item.rrp">{{item.rrp.currency}}</span>
                <span class="price__price" v-else-if="item.price" :data-price="item.price.price">{{item.price.price}}</span> <span class="currency" v-if="item.price && !item.rrp">{{item.price.currency}}</span>
                <span class="price__price" v-else-if="!item.rrp && !item.price"></span>
              </span>
            </span>
            <span class="vat" v-if="item.price && showPrice === '1' && shopEnabled === '1'" v-html="$t('card.vat')"></span>
            <span class="vat" v-else-if="!item.price && showPrice === '1' && shopEnabled === '1'">{{ $t('card.price')}}</span>
<!--            <span class="meta -color-grey" v-if="item.webSiteOzitoTitle">{{item.description}}</span>-->
          </div>
        </div>
        <div class="bottom__addition" v-if="showComparelistButton !== '0'">
          <div class="addition__cta">
            <div class="cta" @click="addToCompareList(item.itemNo)" @keydown.enter="addToCompareList(item.itemNo)" tabindex="0">
              <span class="compare__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="22"><g fill="none" fill-rule="nonzero"><path fill="#E31F25" d="M8.816 19.889c4.87 0 8.817-3.98 8.817-8.889 0-4.91-3.948-8.889-8.817-8.889S0 6.091 0 11c0 4.91 3.947 8.889 8.816 8.889Zm0-1.111c-4.26 0-7.714-3.482-7.714-7.778 0-4.296 3.454-7.778 7.714-7.778S16.531 6.704 16.531 11c0 4.296-3.454 7.778-7.715 7.778Z"/><path fill="#FFF" d="M17.082 21C22.559 21 27 16.523 27 11S22.56 1 17.082 1c-5.478 0-9.919 4.477-9.919 10s4.44 10 9.919 10Zm0-3.333c-3.652 0-6.613-2.985-6.613-6.667s2.96-6.667 6.613-6.667c3.651 0 6.612 2.985 6.612 6.667s-2.96 6.667-6.612 6.667Z"/><path fill="#E31F25" d="M21.812 3.483a.556.556 0 1 1-.597.937 7.636 7.636 0 0 0-2.389-.999.556.556 0 1 1 .25-1.083 8.747 8.747 0 0 1 2.736 1.145Zm3.61 4.597a.556.556 0 1 1-1.051.36 7.8 7.8 0 0 0-1.238-2.278.556.556 0 0 1 .875-.686 8.91 8.91 0 0 1 1.414 2.604Zm-.027 5.86a.556.556 0 0 1-1.049-.368 7.937 7.937 0 0 0 .445-2.544.556.556 0 1 1 1.112.01 8.983 8.983 0 0 1-.508 2.902Zm-3.65 4.584a.556.556 0 1 1-.593-.94 7.799 7.799 0 0 0 1.932-1.729.556.556 0 1 1 .868.694 8.91 8.91 0 0 1-2.207 1.975Zm-5.77 1.296a.556.556 0 1 1 .138-1.103c.869.108 1.74.066 2.586-.121a.556.556 0 1 1 .24 1.084 8.715 8.715 0 0 1-2.963.14Zm-5.184-2.561a.556.556 0 1 1 .794-.778 7.626 7.626 0 0 0 2.1 1.516.556.556 0 0 1-.488.998 8.737 8.737 0 0 1-2.406-1.736Zm-2.495-5.321a.556.556 0 1 1 1.106-.113c.088.868.322 1.712.69 2.5a.556.556 0 0 1-1.007.47 8.892 8.892 0 0 1-.789-2.857Zm1.332-5.682a.556.556 0 0 1 .94.593 7.825 7.825 0 0 0-.998 2.393.556.556 0 0 1-1.082-.253c.229-.979.615-1.899 1.14-2.733Zm4.567-3.646a.556.556 0 1 1 .37 1.048 7.777 7.777 0 0 0-2.268 1.258.556.556 0 0 1-.693-.869 8.888 8.888 0 0 1 2.591-1.437Zm4.861-.276a.556.556 0 0 1-.247 1.083 7.671 7.671 0 0 0-1.685-.194.556.556 0 0 1 .004-1.11c.644.001 1.29.075 1.928.22Z"/><g fill="#E31F25"><path d="M14.59 12.747a.556.556 0 0 1 .656.891l-.08.06-2.756 1.666a.556.556 0 0 1-.656-.891l.08-.06 2.756-1.666ZM15.132 9.974a.556.556 0 0 1 .672.88l-.08.06-4.407 2.778a.556.556 0 0 1-.672-.879l.08-.06 4.407-2.779ZM14.581 7.752a.556.556 0 0 1 .672.88l-.08.06-4.407 2.778a.556.556 0 0 1-.673-.88l.08-.06 4.408-2.778ZM13.488 6.08a.556.556 0 0 1 .656.892l-.08.059-2.756 1.667a.556.556 0 0 1-.656-.892l.08-.06 2.756-1.666Z"/></g></g></svg>
              </span>
              <span class="meta">{{ $t('card.comparebtn')}}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
